import React, { useRef, useState, useEffect } from 'react';
import { Bold, Italic, Underline, List, ListOrdered, Link2, Heading1, Heading2, AlignLeft, AlignCenter, AlignRight } from 'lucide-react';

interface RichTextEditorProps {
  label?: string;
  description?: string;
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  minHeight?: string;
}

export function RichTextEditor({ label, description, value = '', onChange, placeholder = 'Escreva aqui...', minHeight = 'min-h-[150px]' }: RichTextEditorProps) {
  const editableRef = useRef<HTMLDivElement>(null);
  const [html, setHtml] = useState(value);

  // Sync initial value only once or if it's strictly externally driven a full refresh
  useEffect(() => {
    if (editableRef.current && value !== html) {
      if (editableRef.current.innerHTML !== value) {
        editableRef.current.innerHTML = value || '';
        setHtml(value || '');
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const execCmd = (cmd: string, val?: string) => {
    document.execCommand(cmd, false, val);
    editableRef.current?.focus();
    handleChange();
  };

  const handleLink = () => {
    const url = prompt('Inserir link (URL):', 'https://');
    if (url) execCmd('createLink', url);
  };

  const handleChange = () => {
    if (editableRef.current) {
      const newHtml = editableRef.current.innerHTML;
      setHtml(newHtml);
      if (onChange) onChange(newHtml);
    }
  };

  return (
    <div className="w-full flex flex-col gap-1.5 focus-within:ring-2 focus-within:ring-indigo-500/20 rounded-xl transition-shadow">
      {(label || description) && (
        <div className="px-1">
          {label && <label className="text-sm font-medium text-gray-900">{label}</label>}
          {description && <p className="text-xs text-gray-500 mt-0.5">{description}</p>}
        </div>
      )}

      <div className="border border-gray-200 rounded-xl bg-white overflow-hidden shadow-sm">
        {/* Toolbar */}
        <div className="flex items-center flex-wrap gap-1 p-2 border-b border-gray-100 bg-gray-50/50">
           <ToolbarButton onClick={() => execCmd('bold')} title="Negrito"><Bold size={16} /></ToolbarButton>
           <ToolbarButton onClick={() => execCmd('italic')} title="Itálico"><Italic size={16} /></ToolbarButton>
           <ToolbarButton onClick={() => execCmd('underline')} title="Sublinhado"><Underline size={16} /></ToolbarButton>
           <div className="w-px h-4 bg-gray-200 mx-1"></div>
           <ToolbarButton onClick={() => execCmd('formatBlock', 'H1')} title="Título 1"><Heading1 size={16} /></ToolbarButton>
           <ToolbarButton onClick={() => execCmd('formatBlock', 'H2')} title="Título 2"><Heading2 size={16} /></ToolbarButton>
           <div className="w-px h-4 bg-gray-200 mx-1"></div>
           <ToolbarButton onClick={() => execCmd('insertUnorderedList')} title="Lista formatada"><List size={16} /></ToolbarButton>
           <ToolbarButton onClick={() => execCmd('insertOrderedList')} title="Lista numerada"><ListOrdered size={16} /></ToolbarButton>
           <div className="w-px h-4 bg-gray-200 mx-1"></div>
           <ToolbarButton onClick={() => execCmd('justifyLeft')} title="Alinhar à Esquerda"><AlignLeft size={16} /></ToolbarButton>
           <ToolbarButton onClick={() => execCmd('justifyCenter')} title="Centralizar"><AlignCenter size={16} /></ToolbarButton>
           <ToolbarButton onClick={() => execCmd('justifyRight')} title="Alinhar à Direita"><AlignRight size={16} /></ToolbarButton>
           <div className="w-px h-4 bg-gray-200 mx-1"></div>
           <ToolbarButton onClick={handleLink} title="Inserir Link"><Link2 size={16} /></ToolbarButton>
        </div>

        {/* Edit Area */}
        <div 
          ref={editableRef}
          contentEditable
          onInput={handleChange}
          onBlur={handleChange}
          data-placeholder={placeholder}
          className={`p-4 outline-none focus:outline-none prose prose-sm max-w-none text-gray-800 ${minHeight} empty:before:content-[attr(data-placeholder)] empty:before:text-gray-400`}
        />
      </div>
    </div>
  );
}

function ToolbarButton({ children, onClick, title }: { children: React.ReactNode, onClick: () => void, title: string }) {
    return (
        <button
            type="button"
            onClick={onClick}
            title={title}
            className="p-1.5 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
        >
            {children}
        </button>
    )
}
